/**
 * PlayScreen - Écran principal du joueur
 * Identique à Flutter PlayScreen avec Drawer latéral, StepRouter, Header fixe
 */

import React, {useEffect, useState, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  useWindowDimensions,
  Animated,
  Easing,
  Modal,
  Image,
  ImageBackground,
  Switch,
} from 'react-native';
import Svg, {Path} from 'react-native-svg';
import {useRoute, useNavigation, DrawerActions} from '@react-navigation/native';
import {usePlayer} from '../contexts/PlayerContext';
import {useTheme} from '../contexts/ThemeContext';
import StepRouter from '../components/StepRouter';
import GameNotificationOverlay, {GameNotification} from '../components/GameNotificationOverlay';
import InventoryDialog from '../components/InventoryDialog';
import GameMenuScreen from '../components/GameMenuScreen';
import PlayDrawer from '../components/PlayDrawer';
import DecorativeFrame from '../components/DecorativeFrame';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  faBars,
  faBook,
  faShoppingBag,
  faCog,
  faMoon,
  faSun,
  faBookOpen,
  faDoorOpen,
  faUser,
  faUserCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';
import storageService from '../services/storageService';
import {Fonts} from '../config/fonts';

// Import des textures de papier
import paperLight from '../assets/img/paper_light.png';
import paperDark from '../assets/img/paper_dark.png';

interface PlayScreenParams {
  adventureURI?: string;
  slot?: number;
}

const PlayScreen: React.FC = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const {width} = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const {currentStep, gameState, currentAdventure, isLoading, isContextReady, loadGame, startSlot, executeAction, choosePath, editorMode, setEditorMode} = usePlayer();
  const {theme, isDarkMode, isReadingMode, toggleTheme, toggleReadingMode} = useTheme();
  const params = (route.params || {}) as PlayScreenParams;
  
  // Détecter le mode édition (ouvert depuis l'éditeur)
  // @ts-ignore - window est disponible en web
  const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && (
    window.opener !== null || 
    window.location.search.includes('editor=true') ||
    window.location.search.includes('cheat=true')
  );
  
  // Debug: afficher l'état du mode édition
  React.useEffect(() => {
    if (Platform.OS === 'web') {
      console.log('[PlayScreen] isEditingMode:', isEditingMode);
      console.log('[PlayScreen] window.opener:', typeof window !== 'undefined' ? window.opener : 'N/A');
      console.log('[PlayScreen] search params:', typeof window !== 'undefined' ? window.location.search : 'N/A');
    }
  }, [isEditingMode]);
  
  // Générer les styles basés sur le thème ET mode lecture
  const styles = React.useMemo(() => createStyles(theme, isReadingMode, isDarkMode), [theme, isReadingMode, isDarkMode]);
  
  // Log du contexte à chaque rendu pour debug
  useEffect(() => {
    console.log(`[PlayScreen] Context state - gameState: ${!!gameState}, currentStep: ${!!currentStep}, isLoading: ${isLoading}, isContextReady: ${isContextReady}`);
    if (gameState) {
      console.log(`[PlayScreen] gameState.uniqueURI: ${gameState.uniqueURI}, gameState.slot: ${gameState.slot}`);
    }
    if (currentStep) {
      console.log(`[PlayScreen] currentStep.numero: ${currentStep.numero}`);
    }
  });
  const [showInventory, setShowInventory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState<GameNotification[]>([]);
  const [showCharacterSheet, setShowCharacterSheet] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;
  const lastTapRef = useRef<number>(0);
  const characterSheetSlideAnim = useRef(new Animated.Value(0)).current;
  const avatarModalScaleAnim = useRef(new Animated.Value(0)).current;
  const avatarModalOpacityAnim = useRef(new Animated.Value(0)).current;

  // Les préférences (mode lecture, thème) sont maintenant gérées par ThemeContext

  // Référence pour éviter les appels multiples à startSlot (fallback uniquement)
  const startSlotCalledRef = useRef(false);
  
  // Fallback : si on a des params mais que le contexte n'est pas prêt après un délai,
  // appeler startSlot directement (cas où GameMenuScreen n'a pas pu attendre)
  useEffect(() => {
    if (params.adventureURI && params.slot && !isContextReady && !isLoading && !startSlotCalledRef.current) {
      // Attendre un peu pour laisser le temps au contexte de se mettre à jour
      const timeout = setTimeout(() => {
        if (!isContextReady && !isLoading && !startSlotCalledRef.current) {
          console.log(`[PlayScreen] Fallback: Context not ready after delay, calling startSlot directly`);
          startSlotCalledRef.current = true;
          startSlot(params.slot!, params.adventureURI!).catch(error => {
            console.error('[PlayScreen] Error starting slot (fallback):', error);
            startSlotCalledRef.current = false;
            Alert.alert('Erreur', `Impossible de démarrer la partie : ${error.message || error}`);
            navigation.goBack();
          });
        }
      }, 500); // Attendre 500ms avant le fallback
      
      return () => clearTimeout(timeout);
    } else if (isContextReady) {
      // Le contexte est prêt, réinitialiser la référence
      startSlotCalledRef.current = false;
    }
  }, [params.adventureURI, params.slot, isContextReady, isLoading, startSlot, navigation]);

  // Animation pour la modal avatar
  useEffect(() => {
    if (showAvatarModal) {
      // Animation d'entrée
      Animated.parallel([
        Animated.spring(avatarModalScaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          tension: 50,
          friction: 7,
        }),
        Animated.timing(avatarModalOpacityAnim, {
          toValue: 1,
          duration: 200,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Animation de sortie
      Animated.parallel([
        Animated.timing(avatarModalScaleAnim, {
          toValue: 0,
          duration: 150,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(avatarModalOpacityAnim, {
          toValue: 0,
          duration: 150,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [showAvatarModal]);

  // Animation entre les steps (fade + scale)
  useEffect(() => {
    if (currentStep) {
      // Reset d'abord pour l'animation d'entrée
      scaleAnim.setValue(1.5);
      opacityAnim.setValue(0);

      // Puis démarrer l'animation vers les valeurs finales
      Animated.parallel([
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [currentStep?.numero]);

  // Afficher les notifications de la step
  useEffect(() => {
    if (currentStep?.notifications && currentStep.notifications.length > 0) {
      console.log(`[PlayScreen] Displaying ${currentStep.notifications.length} notifications from step`);
      // Convertir PlayerNotification en GameNotification
      const gameNotifications: GameNotification[] = currentStep.notifications.map((notif, index) => ({
        id: `step-${currentStep.numero}-${index}-${Date.now()}`,
        message: notif.description 
          ? `${notif.title}: ${notif.description}`
          : notif.title,
        type: notif.isCheat ? 'warning' : 'success',
        duration: 5000,
      }));
      setNotifications(prev => {
        // Éviter les doublons en vérifiant les IDs
        const existingIds = new Set(prev.map(n => n.id));
        const newNotifications = gameNotifications.filter(n => !existingIds.has(n.id));
        return [...prev, ...newNotifications];
      });
    }
  }, [currentStep?.numero, currentStep?.notifications]);

  // Animation de la feuille de personnage
  useEffect(() => {
    if (showCharacterSheet) {
      // Ouverture : fade + slide
      Animated.parallel([
        Animated.timing(characterSheetSlideAnim, {
          toValue: 1,
          duration: 250,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Fermeture : plus rapide
      Animated.timing(characterSheetSlideAnim, {
        toValue: 0,
        duration: 200,
        easing: Easing.in(Easing.ease),
        useNativeDriver: true,
      }).start();
    }
  }, [showCharacterSheet]);

  const handleDismissNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const addNotification = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const notification: GameNotification = {
      id: Date.now().toString(),
      message,
      type,
      duration: 3000,
    };
    setNotifications(prev => [...prev, notification]);
  };

  const handleMenuAction = async (action: string) => {
    switch (action) {
      case 'theme': {
        toggleTheme();
        break;
      }
      case 'reading': {
        // Le mode lecture est maintenant géré par ThemeContext
        toggleReadingMode();
        break;
      }
      case 'quit':
        // En mode édition (popup), fermer la fenêtre du navigateur
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.opener) {
          console.log('[PlayScreen] In popup/editing mode, closing window');
          window.close();
        } else {
          navigation.navigate('Main' as never);
        }
        break;
    }
  };

  // Si pas de contexte prêt et pas de params, naviguer vers le menu
  // Cela évite les problèmes de Context car GameMenuScreen est un écran de navigation
  // IMPORTANT: Ce useEffect doit être AVANT tous les returns conditionnels pour respecter les règles des hooks
  // IMPORTANT: Ne naviguer que si on n'a vraiment pas de contexte ET pas de params ET pas de chargement en cours
  // Ne pas naviguer pendant les opérations normales (choosePath, executeAction) qui mettent isLoading à true temporairement
  // NOTE: En mode test web (/test/:adventureURI/step/:stepNumber via hash), on NE DOIT PAS rediriger vers Main,
  // car le contexte est initialisé via initTestGame depuis AppNavigator, sans params de navigation.
  const hasNavigatedRef = useRef(false);
  useEffect(() => {
    // Détection du mode test web basé sur le hash (même logique que AppNavigator)
    let isWebTestRoute = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const rawHash = window.location.hash || '';
      const hashPath = rawHash.startsWith('#') ? rawHash.substring(1) : rawHash;
      isWebTestRoute = hashPath.startsWith('/test/');
    }

    // Ne naviguer que si :
    // 1. On n'a pas déjà navigué (éviter les déclenchements multiples)
    // 2. On n'a pas de contexte prêt
    // 3. On n'a pas de params (pas de navigation depuis GameMenuScreen)
    // 4. On n'est pas en train de charger (éviter pendant choosePath/executeAction)
    // 5. On n'a pas de gameState du tout (vraiment vide, pas juste temporairement)
    // 6. On n'est PAS en mode test web (dans ce cas on attend initTestGame qui se charge du contexte)
    if (!hasNavigatedRef.current && !isContextReady && !params.adventureURI && !isLoading && !gameState && !isWebTestRoute) {
      console.log('[PlayScreen] No context ready and no params and no gameState, navigating to Main');
      hasNavigatedRef.current = true;
      navigation.navigate('Main' as never);
    }
    // Réinitialiser le flag si le contexte redevient prêt (pour permettre une nouvelle navigation si nécessaire)
    if (isContextReady) {
      hasNavigatedRef.current = false;
    }
  }, [isContextReady, params.adventureURI, isLoading, gameState, navigation]);

  // Tous les hooks doivent être appelés avant les returns conditionnels
  // Si le contexte n'est pas prêt, afficher un loader
  // Le flag isContextReady garantit que gameState et currentStep sont vraiment disponibles
  if (!isContextReady) {
    // Si on a des params, c'est qu'on vient de GameMenuScreen et on doit attendre que le contexte se mette à jour
    if (params.adventureURI && params.slot) {
      console.log(`[PlayScreen] Waiting for context to be ready (isContextReady: ${isContextReady}, isLoading: ${isLoading})`);
      // Afficher un loader pendant qu'on attend
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Chargement...</Text>
        </View>
      );
    }
    
    // Si pas de params, afficher aussi un loader (ne devrait pas arriver souvent)
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Chargement...</Text>
      </View>
    );
  }

  // Récupérer le nombre de notes dans le notebook
  // Compter les entrées dans notebookAuto et notebookManual (tableaux)
  const notebookAuto = Array.isArray(gameState?.data.notebookAuto) ? gameState.data.notebookAuto : [];
  const notebookManual = Array.isArray(gameState?.data.notebookManual) ? gameState.data.notebookManual : [];
  const notebookCount = notebookAuto.length + notebookManual.length;
  const inventoryCount = gameState?.data.objects?.length || 0;

  // Récupérer l'avatar depuis gameState ou currentStep
  const avatarImage =
    gameState?.data.layerAdventurerId ||
    (currentStep?.layerAdventurer?.['type'] === 'picture' ? currentStep.layerAdventurer?.['image'] : null);

  const buildImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  const avatarUri = buildImageUri(avatarImage);

  // Le drawer peut toujours être ouvert via swipe

  return (
    <View style={[styles.container, isReadingMode && styles.containerReadingMode]}>
      {/* Texture papier : masquée en mode lecture (liseuse) */}
      {!isReadingMode && (
        <ImageBackground
          source={isDarkMode ? paperDark : paperLight}
          style={StyleSheet.absoluteFill}
          imageStyle={styles.backgroundImage}
          resizeMode="stretch"
        />
      )}
      
      {/* Cadre décoratif : uniquement en mode normal (pas en mode lecture) */}
      {!isReadingMode && <DecorativeFrame topOffset={55} bottomOffset={isEditingMode ? 50 : 0} />}
      
      <GameNotificationOverlay
        notifications={notifications}
        onDismiss={handleDismissNotification}
      />

      <InventoryDialog visible={showInventory} onClose={() => setShowInventory(false)} />

      {/* Zone de lecture immersive - fond papier */}
      <TouchableOpacity
        style={styles.readingSurface}
        activeOpacity={1}
        onPress={() => {
          // Double-tap pour naviguer vers les paramètres de jeu
          const now = Date.now();
          if (now - lastTapRef.current < 300) {
            // GameSettings utilise maintenant directement ThemeContext
            navigation.navigate('GameSettings' as never);
          }
          lastTapRef.current = now;
        }}>
        {/* Header invisible avec titre ou ornement dans la zone safe area */}
        {!showCharacterSheet && !isReadingMode && (
          <View style={[styles.headerContainer, {top: insets.top + 12}]}>
            {currentStep?.name ? (
              <Text style={styles.headerTitle} numberOfLines={2}>
                {currentStep.name}
              </Text>
            ) : (
              <Svg width="120" height="50" viewBox="0 0 120 50" style={{opacity: 0.5}}>
                {/* Ornement symétrique élégant - motif de livre ancien */}
                <Path
                  d="M 60 5 L 50 15 L 40 10 L 30 20 L 20 15 L 10 25 M 60 5 L 70 15 L 80 10 L 90 20 L 100 15 L 110 25"
                  fill="none"
                  stroke={isDarkMode ? '#8B7355' : '#8B7355'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <Path
                  d="M 60 10 Q 50 12 45 18 Q 42 20 38 20 M 60 10 Q 70 12 75 18 Q 78 20 82 20"
                  fill="none"
                  stroke={isDarkMode ? '#A68968' : '#A68968'}
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                {/* Point central */}
                <Path
                  d="M 60 8 m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0"
                  fill={isDarkMode ? '#8B7355' : '#8B7355'}
                />
              </Svg>
            )}
          </View>
        )}
        
        {/* Icône/Avatar discrète profil en haut à gauche */}
        {!showCharacterSheet && (
          <TouchableOpacity
            style={[styles.characterSheetButton, {top: insets.top + 12, left: 12}]}
            onPress={() => setShowCharacterSheet(true)}
            activeOpacity={0.7}>
            {avatarUri ? (
              <Image
                source={{uri: avatarUri}}
                style={styles.characterSheetButtonImage}
                resizeMode="cover"
              />
            ) : (
              <FontAwesomeIcon 
                icon={faUserCircle} 
                size={22} 
                color={isDarkMode ? '#C8B8A8' : '#8B7355'} 
              />
            )}
          </TouchableOpacity>
        )}
        
        {/* Boutons en haut à droite : Options */}
        {!showCharacterSheet && (
          <View style={[styles.headerRightButtons, {top: insets.top + 12, right: 12}]}>
            {/* Icône options - Ouvrir les paramètres de jeu */}
            <TouchableOpacity
              style={styles.optionsButton}
              onPress={() => {
                // GameSettings utilise maintenant directement ThemeContext
                navigation.navigate('GameSettings' as never);
              }}
              activeOpacity={0.7}>
              <FontAwesomeIcon icon={faCog} size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          </View>
        )}
        
        {/* Contenu avec StepRouter - masqué si feuille de personnage ouverte */}
        {!showCharacterSheet && (
          <View style={[styles.content, {
            paddingTop: insets.top + 95, 
            paddingBottom: (isEditingMode && currentStep && gameState?.state !== 'SUCCEED' && gameState?.state !== 'FAILED') ? 60 : insets.bottom + 24
          }]}>
            <Animated.View
              style={[
                styles.stepContainer,
                {
                  transform: [{scale: scaleAnim}],
                  opacity: opacityAnim,
                },
              ]}>
              {currentStep && <StepRouter step={currentStep} />}
            </Animated.View>
          </View>
        )}
      </TouchableOpacity>

      {/* Feuille de personnage - Overlay plein écran */}
      <Modal
        visible={showCharacterSheet}
        transparent
        animationType="none"
        onRequestClose={() => setShowCharacterSheet(false)}>
        <Animated.View
          style={[
            styles.characterSheetOverlay,
            {
              opacity: characterSheetSlideAnim,
              transform: [
                {
                  translateY: characterSheetSlideAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [50, 0],
                  }),
                },
              ],
            },
          ]}>
          <View style={styles.characterSheetContainer}>
            {/* Texture papier */}
            <View style={styles.characterSheetTexture} />
            
            {/* Contenu de la feuille */}
            <View style={styles.characterSheetContent}>
              <PlayDrawer
                isDarkMode={isDarkMode}
                onClose={() => setShowCharacterSheet(false)}
                asOverlay={true}
                initialTab={notebookCount > 0 ? 'notes' : 'stats'}
                onAvatarPress={() => {
                  // Ouvrir la modal avatar sans fermer la fiche de personnage ni l'inventaire
                  setShowAvatarModal(true);
                }}
              />
            </View>
          </View>
        </Animated.View>
      </Modal>

      {/* Modal Avatar en grand - Style élégant */}
      <Modal
        visible={showAvatarModal}
        transparent
        animationType="none"
        onRequestClose={() => setShowAvatarModal(false)}
        statusBarTranslucent={true}>
        <Animated.View
          style={[
            styles.avatarModalOverlay,
            {
              opacity: avatarModalOpacityAnim,
            },
          ]}>
          <TouchableOpacity
            style={styles.avatarModalBackdrop}
            activeOpacity={1}
            onPress={() => setShowAvatarModal(false)}>
            <View style={styles.avatarModalCenteredContainer}>
              <Animated.View
                style={[
                  styles.avatarModalContent,
                  {
                    transform: [
                      {
                        scale: avatarModalScaleAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0.85, 1],
                        }),
                      },
                    ],
                  },
                ]}
                onStartShouldSetResponder={() => true}
                onTouchEnd={(e) => e.stopPropagation()}>
              {/* Bouton fermer élégant */}
              <TouchableOpacity
                style={styles.avatarModalCloseButton}
                onPress={() => setShowAvatarModal(false)}
                activeOpacity={0.7}>
                <FontAwesomeIcon icon={faTimes} size={18} color="#E8E6E1" />
              </TouchableOpacity>

              {/* Avatar en grand avec cadre élégant */}
              {(() => {
                const avatarImage =
                  gameState?.data.layerAdventurerId ||
                  (currentStep?.layerAdventurer?.['type'] === 'picture'
                    ? currentStep.layerAdventurer?.['image']
                    : null);
                const buildImageUri = (imageData?: string): string | null => {
                  if (!imageData) return null;
                  if (imageData.startsWith('data:image')) {
                    return imageData;
                  }
                  return `data:image/png;base64,${imageData}`;
                };
                const avatarUri = buildImageUri(avatarImage as string);

                return (
                  <View style={styles.avatarModalImageWrapper}>
                    <View style={styles.avatarModalFrame}>
                      <View style={styles.avatarModalImageContainer}>
                        {avatarUri ? (
                          <Image source={{uri: avatarUri}} style={styles.avatarModalImage} />
                        ) : (
                          <View style={styles.avatarModalPlaceholder}>
                            <FontAwesomeIcon icon={faUserCircle} size={120} color="#E8E6E1" />
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                );
              })()}
              </Animated.View>
            </View>
          </TouchableOpacity>
        </Animated.View>
      </Modal>

      {/* Footer fixe avec Mode éditeur - Seulement en mode édition */}
      {isEditingMode && currentStep && gameState?.state !== 'SUCCEED' && gameState?.state !== 'FAILED' && (
        <View style={styles.cheatFooter}>
          <Text style={styles.cheatFooterLabel}>Mode éditeur</Text>
          <Switch
            value={editorMode || false}
            onValueChange={(value) => {
              console.log(`[PlayScreen] Toggling editor mode: ${value}`);
              setEditorMode(value);
            }}
            trackColor={{false: '#D4C4B0', true: '#8B7355'}}
            thumbColor={editorMode ? '#F5F3ED' : '#E8D5C4'}
          />
        </View>
      )}
    </View>
  );
};

// Fonction pour créer les styles basés sur le thème ET le mode lecture
const createStyles = (theme: any, isReadingMode: boolean, isDarkMode: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent', // Transparent pour laisser voir l'ImageBackground
  },
  containerReadingMode: {
    backgroundColor: isReadingMode 
      ? (theme.colors.bg === '#EFE8DF' || theme.colors.bg === '#FBF8F2' ? '#FFFEF9' : '#1C1A17') // Liseuse : blanc crème ou noir pur
      : theme.colors.bg,
  },
  readingSurface: {
    flex: 1,
    backgroundColor: 'transparent',
    position: 'relative',
  },
  paperTexture: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.025, // Texture papier très légère (2-3%)
    backgroundColor: '#000',
    ...Platform.select({
      web: {
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      },
    }),
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: theme.colors.textMuted,
  },
  cornerButton: {
    position: 'absolute',
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 5,
      },
    }),
  },
  cornerButtonLeft: {
    left: 12,
  },
  cornerButtonRight: {
    right: 12,
  },
  avatarButton: {
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  avatarIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  cornerBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FFA726',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#fff',
  },
  cornerBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  headerContainer: {
    position: 'absolute',
    left: 64, // Plus centré
    right: 64,
    zIndex: 100,
    minHeight: 32,
    maxHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent', // Toujours transparent
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 0,
  },
  headerTitle: {
    fontSize: isReadingMode ? 15 : 14,
    fontWeight: isReadingMode ? '500' : '600',
    color: isDarkMode ? 'rgba(232, 226, 218, 0.85)' : 'rgba(90, 74, 58, 0.75)',
    textAlign: 'center',
    fontFamily: Fonts.title,
    letterSpacing: 0.8,
    lineHeight: 18,
    textTransform: 'uppercase', // Petites majuscules élégantes
    ...Platform.select({
      web: {
        textShadow: isDarkMode 
          ? '0 1px 3px rgba(0, 0, 0, 0.6), 0 0 8px rgba(0, 0, 0, 0.3)' 
          : '0 1px 3px rgba(255, 255, 255, 0.9)',
      },
    }),
  },
  content: {
    flex: 1,
  },
  stepContainer: {
    flex: 1,
  },
  characterSheetButton: {
    position: 'absolute',
    zIndex: 101,
    width: 42,
    height: 42,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDarkMode 
      ? 'rgba(44, 40, 36, 0.92)' 
      : 'rgba(255, 252, 248, 0.92)',
    borderRadius: 21,
    overflow: 'hidden',
    borderWidth: 1.5,
    borderColor: isDarkMode 
      ? 'rgba(90, 82, 74, 0.5)' 
      : 'rgba(212, 196, 176, 0.4)',
    ...Platform.select({
      web: {
        boxShadow: isDarkMode 
          ? '0 2px 6px rgba(0, 0, 0, 0.35), inset 0 1px 1px rgba(255, 255, 255, 0.05)' 
          : '0 2px 6px rgba(139, 115, 85, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(6px)',
      },
      default: {
        shadowColor: isDarkMode ? '#000' : '#8B7355',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: isDarkMode ? 0.35 : 0.18,
        shadowRadius: 3,
        elevation: 3,
      },
    }),
  },
  characterSheetButtonImage: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
  },
  headerRightButtons: {
    position: 'absolute',
    zIndex: 101, // Au-dessus du cadre décoratif
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  optionsButton: {
    width: 42,
    height: 42,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDarkMode 
      ? 'rgba(44, 40, 36, 0.92)' 
      : 'rgba(255, 252, 248, 0.92)',
    borderRadius: 21,
    borderWidth: 1.5,
    borderColor: isDarkMode 
      ? 'rgba(90, 82, 74, 0.5)' 
      : 'rgba(212, 196, 176, 0.4)',
    ...Platform.select({
      web: {
        boxShadow: isDarkMode 
          ? '0 2px 6px rgba(0, 0, 0, 0.35), inset 0 1px 1px rgba(255, 255, 255, 0.05)' 
          : '0 2px 6px rgba(139, 115, 85, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(6px)',
      },
      default: {
        shadowColor: isDarkMode ? '#000' : '#8B7355',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: isDarkMode ? 0.35 : 0.18,
        shadowRadius: 3,
        elevation: 3,
      },
    }),
  },
  characterSheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)', // Fond semi-transparent pour masquer l'histoire
  },
  characterSheetContainer: {
    flex: 1,
    backgroundColor: theme.colors.bg,
    position: 'relative',
  },
  characterSheetTexture: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.025,
    backgroundColor: '#000',
    ...Platform.select({
      web: {
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      },
    }),
  },
  characterSheetContent: {
    flex: 1,
  },
  avatarModalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999, // Au-dessus de tout, notamment l'inventaire
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  avatarModalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // Fond sombre avec transparence
    ...Platform.select({
      web: {
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      },
    }),
    zIndex: 10000,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarModalCenteredContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10001,
  },
  avatarModalContent: {
    width: '85%',
    maxWidth: 600,
    aspectRatio: 1,
    backgroundColor: 'rgba(245, 243, 237, 0.98)', // Fond beige clair élégant
    borderRadius: 24,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    ...Platform.select({
      web: {
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 115, 85, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.4,
        shadowRadius: 24,
        elevation: 12,
      },
    }),
    borderWidth: 3,
    borderColor: '#D4C4B0', // Bordure beige dorée
  },
  avatarModalCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(139, 115, 85, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderWidth: 2,
    borderColor: 'rgba(212, 196, 176, 0.5)',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 4,
      },
    }),
  },
  avatarModalImageWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarModalFrame: {
    width: '100%',
    height: '100%',
    padding: 16,
    backgroundColor: '#F5F3ED',
    borderRadius: 16,
    ...Platform.select({
      web: {
        boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.1)',
      },
    }),
    borderWidth: 2,
    borderColor: '#E8D5C4',
    borderStyle: 'solid',
  },
  avatarModalImageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.colors.surface2,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 4,
      },
    }),
  },
  avatarModalImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  avatarModalPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E8D5C4',
  },
  backgroundImage: {
    opacity: 1.0, // 100% visible
    width: '100%',
    height: '100%',
  },
  // Footer fixe pour le mode éditeur
  cheatFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    marginBottom: 0,
    backgroundColor: isReadingMode ? theme.colors.surface : theme.colors.surface3,
    borderTopWidth: isReadingMode ? 0.5 : 1,
    borderTopColor: theme.colors.border,
    opacity: isReadingMode ? 0.9 : 1,
    zIndex: 1000,
    ...theme.elevation.header,
  },
  cheatFooterLabel: {
    fontSize: isReadingMode ? 12 : 13,
    fontWeight: '500',
    color: theme.colors.textMuted,
    fontFamily: Fonts.reading,
  },
});

export default PlayScreen;